import { Trans } from '@lingui/macro'
import { Currency, CurrencyAmount, Percent } from '@uniswap/sdk-core'
import { Position } from '@uniswap/v3-sdk'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import Loader from 'components/Icons/LoadingSpinner'
import { RowFixed } from 'components/Row'
import { useToken } from 'hooks/Tokens'
import { PositionWithOwner } from 'hooks/useAllAdminPositions'
import { usePool } from 'hooks/usePools'
import { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components/macro'
import { ThemedText } from 'theme'
import { formatCurrencyAmount } from 'utils/formatCurrencyAmount'
import { unwrappedToken } from 'utils/unwrappedToken'

import { PositionFeesData, PositionFeesFetcher } from './PositionFeesFetcher'

const PoolRow = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.backgroundOutline};

  &:last-child {
    border-bottom: none;
  }
`

const PoolHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
  @media screen and (max-width: ${({ theme }) => theme.breakpoint.sm}px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const FeeTierText = styled(ThemedText.UtilityBadge)`
  font-size: 10px !important;
  margin-left: 14px !important;
`

const FeesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  width: 100%;
  margin-top: 12px;
`

const LiquidityGrid = styled(FeesGrid)``

const FeeItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.backgroundModule};
  @media screen and (max-width: ${({ theme }) => theme.breakpoint.sm}px) {
    flex-direction: column;
  }
`

const PositionCount = styled(ThemedText.Caption)`
  color: ${({ theme }) => theme.textSecondary};
  margin-left: auto;
`

interface PoolFeesRowProps {
  positions: PositionWithOwner[]
}

export default function PoolFeesRow({ positions }: PoolFeesRowProps) {
  const firstPosition = positions[0]
  const token0 = useToken(firstPosition.token0)
  const token1 = useToken(firstPosition.token1)

  const currency0 = token0 ? unwrappedToken(token0) : undefined
  const currency1 = token1 ? unwrappedToken(token1) : undefined

  const [, pool] = usePool(currency0 ?? undefined, currency1 ?? undefined, firstPosition.fee)

  const [feesMap, setFeesMap] = useState<Map<string, PositionFeesData>>(new Map())

  const handleFeesLoaded = useCallback((data: PositionFeesData) => {
    setFeesMap((prev) => {
      const next = new Map(prev)
      next.set(data.tokenId, data)
      return next
    })
  }, [])

  const allFeesLoaded = feesMap.size === positions.length
  const someFeesLoaded = feesMap.size > 0

  const { totalFee0, totalFee1 } = useMemo(() => {
    let totalFee0: CurrencyAmount<Currency> | undefined
    let totalFee1: CurrencyAmount<Currency> | undefined

    for (const [, data] of feesMap) {
      if (data.fee0) {
        totalFee0 = totalFee0 ? totalFee0.add(data.fee0) : data.fee0
      }
      if (data.fee1) {
        totalFee1 = totalFee1 ? totalFee1.add(data.fee1) : data.fee1
      }
    }

    return { totalFee0, totalFee1 }
  }, [feesMap])

  const { totalLiquidity0, totalLiquidity1 } = useMemo(() => {
    if (!pool || !currency0 || !currency1) return { totalLiquidity0: undefined, totalLiquidity1: undefined }

    let totalLiquidity0: CurrencyAmount<Currency> | undefined
    let totalLiquidity1: CurrencyAmount<Currency> | undefined

    for (const pos of positions) {
      if (pos.liquidity.isZero()) continue

      try {
        const position = new Position({
          pool,
          liquidity: pos.liquidity.toString(),
          tickLower: pos.tickLower,
          tickUpper: pos.tickUpper,
        })

        const amount0 = CurrencyAmount.fromRawAmount(currency0, position.amount0.quotient.toString())
        const amount1 = CurrencyAmount.fromRawAmount(currency1, position.amount1.quotient.toString())

        totalLiquidity0 = totalLiquidity0 ? totalLiquidity0.add(amount0) : amount0
        totalLiquidity1 = totalLiquidity1 ? totalLiquidity1.add(amount1) : amount1
      } catch {
        // Some positions might fail to construct (rare edge cases with out-of-range ticks).
      }
    }

    return { totalLiquidity0, totalLiquidity1 }
  }, [pool, positions, currency0, currency1])

  const activePositions = useMemo(() => positions.filter((p) => !p.liquidity?.isZero()).length, [positions])

  const totalPositions = positions.length

  return (
    <PoolRow>
      <PoolHeader>
        <RowFixed>
          <DoubleCurrencyLogo currency0={currency0} currency1={currency1} size={20} margin />
          <ThemedText.SubHeader style={{ marginLeft: '4px' }}>
            {currency0?.symbol} / {currency1?.symbol}
          </ThemedText.SubHeader>
          <FeeTierText>
            <Trans>{new Percent(firstPosition.fee, 1_000_000).toSignificant()}%</Trans>
          </FeeTierText>
        </RowFixed>
        <PositionCount>
          {activePositions} / {totalPositions} <Trans>active positions</Trans>
        </PositionCount>
      </PoolHeader>

      <FeesGrid>
        <FeeItem>
          <ThemedText.Caption color="textSecondary">
            {currency0?.symbol} <Trans>fees</Trans>
          </ThemedText.Caption>
          <ThemedText.SubHeader>
            {!someFeesLoaded ? <Loader /> : formatCurrencyAmount(totalFee0, 6)}
          </ThemedText.SubHeader>
        </FeeItem>
        <FeeItem>
          <ThemedText.Caption color="textSecondary">
            {currency1?.symbol} <Trans>fees</Trans>
          </ThemedText.Caption>
          <ThemedText.SubHeader>
            {!someFeesLoaded ? <Loader /> : formatCurrencyAmount(totalFee1, 6)}
          </ThemedText.SubHeader>
        </FeeItem>
      </FeesGrid>

      <LiquidityGrid>
        <FeeItem>
          <ThemedText.Caption color="textSecondary">
            {currency0?.symbol} <Trans>liquidity</Trans>
          </ThemedText.Caption>
          <ThemedText.SubHeader>{!pool ? <Loader /> : formatCurrencyAmount(totalLiquidity0, 10)}</ThemedText.SubHeader>
        </FeeItem>
        <FeeItem>
          <ThemedText.Caption color="textSecondary">
            {currency1?.symbol} <Trans>liquidity</Trans>
          </ThemedText.Caption>
          <ThemedText.SubHeader>{!pool ? <Loader /> : formatCurrencyAmount(totalLiquidity1, 10)}</ThemedText.SubHeader>
        </FeeItem>
      </LiquidityGrid>

      {!allFeesLoaded && someFeesLoaded && (
        <ThemedText.Caption color="textSecondary" style={{ marginTop: '4px' }}>
          <Trans>
            Loading fees... ({feesMap.size}/{positions.length})
          </Trans>
        </ThemedText.Caption>
      )}

      {positions.map((pos) => (
        <PositionFeesFetcher
          key={pos.tokenId.toString()}
          pool={pool}
          tokenId={pos.tokenId}
          onFeesLoaded={handleFeesLoaded}
        />
      ))}
    </PoolRow>
  )
}
